import { Grid, LinearProgress, List } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import { createStyles, makeStyles } from "@material-ui/styles";
import produce from "immer";
import React, { FC, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router";
import { useGetExamSessionQuery, useUpdateExpertAssignmentsMutation } from "../../api/rtk/ExamSessionApi";
import { useGetExpertsQuery } from "../../api/rtk/ExpertApi";
import { ExpertRole, getExpertRoleTranslationKey } from "../../data/constants";
import { AssignedUserViewModel } from "../../model/AssignedUserViewModel";
import { ExamDayViewModel } from "../../model/ExamDayViewModel";
import { GetLocalizedName } from "../../model/ExamSessionViewModel";
import { ExpertAssignmentRequestModel } from "../../model/ExpertAssignmentRequestModel";
import { RoomAssignmentViewModel } from "../../model/RoomAssignmentViewModel";
import { routesPaths } from "../../Routes";
import { GetTranslation } from "../../translations/GetTranslation";
import DefaultButtons from "../common/buttons/DefaultButtons";
import CustomSwitch from "../common/CustomSwitch";
import LocaleFormatDate from "../common/dates/LocaleFormatDate";
import FormGridCell from "../common/FormGridCell";
import TranslatedMessage from "../translations/TranslatedMessage";
import RoomAssignmentRow from "./RoomAssignmentRow";

type RoomAssignmentBoardProps = {
  expertRole: ExpertRole;
  assignPreparationRooms: boolean;
  fixedExpertsLabel: JSX.Element;
  fixedExpertsPlaceholder: JSX.Element;
  reserveExpertsLabel: JSX.Element;
  reserveExpertsPlaceholder: JSX.Element;
  minimumFixedUsers: number;
};

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: "100%",
    },
    inline: {
      display: "inline",
    },
  })
);

const RoomAssignmentBoard: FC<RoomAssignmentBoardProps> = ({
  expertRole,
  assignPreparationRooms,
  minimumFixedUsers,
  ...labelsAndPlaceholders
}) => {
  const classes = useStyles();
  const history = useHistory();
  const intl = useIntl();
  const { examSessionId, examDayId } = useParams<{ examSessionId: string; examDayId: string }>();
  const [examDay, setExamDay] = useState<ExamDayViewModel | undefined>();
  const [showAllExperts, setShowAllExperts] = useState<boolean>(false);
  const [updateExpertAssignments, { isLoading: isUpdateExpertAssignmentsLoading }] =
    useUpdateExpertAssignmentsMutation();
  const { data: examSession, isFetching: isExamSessionFetching } = useGetExamSessionQuery(examSessionId);
  const { data: allExperts } = useGetExpertsQuery();
  useEffect(() => {
    const examDay = examSession?.examDays.find((examDay) => examDay.id === examDayId);
    setExamDay(examDay);
  }, [examDayId, examSession]);

  if (isExamSessionFetching || isUpdateExpertAssignmentsLoading) {
    return <LinearProgress variant="query" />;
  }

  if (examSession === undefined || examDay === undefined) {
    return <></>;
  }

  const filterRoomAssignments = (roomAssignments: RoomAssignmentViewModel[], assignPreparationRooms: boolean) =>
    roomAssignments.filter((roomAssignment) =>
      assignPreparationRooms ? roomAssignment.isPreparationRoom : !roomAssignment.isPreparationRoom
    );

  const handleSave = async () => {
    const data: ExpertAssignmentRequestModel[] = examDay.roomAssignments.map((roomAssignment) => ({
      roomId: roomAssignment.roomId,
      assignedUsers: roomAssignment.assignedUsers,
    }));
    updateExpertAssignments({
      examSessionId,
      examDayId: examDay.id,
      data,
    });
  };

  const handleClose = () => {
    const path = routesPaths.examDay(examSessionId, examDay.locationId, examDay.id);
    history.push(path);
  };

  const updateAssignedExperts = (
    roomAssignment: RoomAssignmentViewModel,
    areFixed: boolean,
    selectedExperts: AssignedUserViewModel[]
  ) => {
    setExamDay((prevDay) =>
      produce(prevDay, (draftDay) => {
        const assignment = draftDay?.roomAssignments.find((assignment) => assignment.id === roomAssignment.id);
        if (assignment === undefined) {
          return;
        }

        assignment.assignedUsers = [
          ...roomAssignment.assignedUsers.filter((user) => user.isFixed !== areFixed),
          ...selectedExperts,
        ];
      })
    );
  };

  const handleChange =
    (roomAssignment: RoomAssignmentViewModel) => (areFixed: boolean, selectedExperts: AssignedUserViewModel[]) => {
      updateAssignedExperts(roomAssignment, areFixed, selectedExperts);
    };

  const handleShowAllExpertsChange = (checked: boolean) => {
    setShowAllExperts(() => checked);
  };

  return (
    <Grid container={true} spacing={0} alignItems="baseline">
      <Grid item={true} sm={12} md={10}>
        <Typography variant="h6" component="span">
          {GetLocalizedName(examSession)}
        </Typography>
        <Typography variant="h4">
          <TranslatedMessage id="userRoomAssignment.assignment" />:{" "}
          {GetTranslation(intl, getExpertRoleTranslationKey(expertRole))}
        </Typography>
      </Grid>
      <Grid item={true} sm={12} md={2}>
        <DefaultButtons
          onClose={handleClose}
          onSave={handleSave}
          isDeleteButtonEnabled={false}
          isDeleteButtonVisible={false}
        />
      </Grid>
      <FormGridCell size="wide">
        <LocaleFormatDate variant="h4" date={examDay.date} />
      </FormGridCell>
      <FormGridCell size="wide">
        <CustomSwitch
          label={<TranslatedMessage id="userRoomAssignment.label.allExpertsSwitch" />}
          value={showAllExperts}
          onChange={handleShowAllExpertsChange}
        />
      </FormGridCell>
      <FormGridCell size="wide">
        <List className={classes.root}>
          {filterRoomAssignments(examDay.roomAssignments, assignPreparationRooms).map((roomAssignment) => {
            return (
              <RoomAssignmentRow
                key={roomAssignment.id}
                {...labelsAndPlaceholders}
                examSession={examSession}
                allExperts={allExperts}
                expertRole={expertRole}
                showAllExperts={showAllExperts}
                minimumFixedUsers={minimumFixedUsers}
                examDay={examDay}
                roomAssignment={roomAssignment}
                change={handleChange(roomAssignment)}
              />
            );
          })}
        </List>
      </FormGridCell>
    </Grid>
  );
};

export default RoomAssignmentBoard;
